'use client';

import dynamic from 'next/dynamic';
import { FC, PointerEvent, useEffect, useRef, useState } from 'react';
import FormattedPrice from '@/components/common/formatted-price/formatted-price';
import { Button, Card, CardBody, CardFooter } from '@/components/ui';
import useCoreConfig from '@/hooks/use-core-config';
import useDeferUntilIdle from '@/hooks/use-defer-until-idle';
import { useGetDiscountType } from '@/hooks/use-has-discount';
import { useKeycloak } from '@/providers/keycloak-provider';
import { MyCartActionButtonsEnum } from '@/types/action-buttons/action-buttons';
import { SimpleProductInfo } from '@/types/products/simple-product-info';
import { cn } from '@/utils';
import type { ContentVariant } from '@/utils/content-variant-from-background';
import { buildGumletUrl } from '@/utils/gumlet';
import { PRODUCT_CARD_IMAGE_PARAMS } from '@/utils/product-listing-lcp-preload';
import { selectMainImage } from '@/utils/select-main-image';
import ListingProductImage from './listing-product-image';
import { ProductCardNavigationWrapper } from './product-card-navigation-wrapper';

const ProductCardInteractiveActions = dynamic(() => import('./product-card-interactive-actions'), {
  ssr: false,
});

const ProductCardAddToCartButton = dynamic(
  () =>
    import('./product-card-interactive-actions').then(module => ({
      default: module.ProductCardAddToCartButton,
    })),
  { ssr: false },
);

export interface ProductCardListingProps {
  product: SimpleProductInfo;
  noMaxWidth?: boolean;
  interactive?: boolean;
  showAddToCart?: boolean;
  showFavorites?: boolean;
  contentVariant?: ContentVariant;
  addToCartButtonType?: MyCartActionButtonsEnum;
  priority?: boolean;
}

enum ProductCardVersions {
  VERSION_1 = 'VERSION_1',
  VERSION_2 = 'VERSION_2',
  VERSION_3 = 'VERSION_3',
}

const HOVER_IMAGE_DELAY_MS = 400;

/**
 * Listing-grid product card: no Framer Motion, Keycloak, cart/favorites hooks, or GTM.
 * Interactive actions load after idle via a separate chunk.
 */
const useProductCardListingPresentation = (
  product: SimpleProductInfo,
  contentVariant: ContentVariant,
) => {
  const {
    variants: { productCard: productCardVersion },
    features,
    productCardStyle,
    currency,
  } = useCoreConfig();
  const { getDiscountType } = useGetDiscountType();
  const { isAuthenticated } = useKeycloak();

  const shouldShowPrice = (!isAuthenticated && features.priceForGuests) || isAuthenticated;
  const shouldAllowCart = (!isAuthenticated && features.cartForGuests) || isAuthenticated;
  const shouldChangeImageOnHover = features.imageChangeOnHover;

  const isDarkSurface = contentVariant === 'dark';
  const primaryPriceColor = isDarkSurface
    ? 'white'
    : productCardVersion === ProductCardVersions.VERSION_2
      ? 'black'
      : 'gray';
  const mutedPriceColor = isDarkSurface ? 'whiteMuted' : 'gray';
  const strikethroughPriceColor = isDarkSurface
    ? 'whiteMuted'
    : productCardVersion === ProductCardVersions.VERSION_2
      ? 'black'
      : 'gray';
  const isVersion2ProductCard = productCardVersion === ProductCardVersions.VERSION_2;
  const productCardFontWeight = isVersion2ProductCard ? ('bold' as const) : undefined;

  const mainImageSrc = selectMainImage(product.images, PRODUCT_CARD_IMAGE_PARAMS);
  const hoverImageSrc =
    shouldChangeImageOnHover && product.images && product.images.length > 1
      ? buildGumletUrl(product.images[1], PRODUCT_CARD_IMAGE_PARAMS)
      : undefined;

  const renderPriceContent = () => {
    const discountType = getDiscountType(product, product.parentCategoryId, product.id);

    if (discountType === 'NONE') {
      return (
        <FormattedPrice
          price={product.price}
          color={productCardStyle?.priceColor ? undefined : primaryPriceColor}
          customColor={productCardStyle?.priceColor}
          productId={product.id}
          fontWeight={productCardFontWeight}
          showVatSuffix
          vatSuffixSize="sm"
          vatSuffixColor={productCardStyle?.vatBadgeColor}
          vatSuffixBackground={productCardStyle?.vatBadgeBackground}
        />
      );
    }

    if (discountType === 'PRODUCT') {
      return (
        <>
          <FormattedPrice
            price={product.discountedPrice}
            color="red"
            productId={product.id}
            fontWeight={productCardFontWeight}
            showVatSuffix
            vatSuffixSize="sm"
            vatSuffixColor={productCardStyle?.vatBadgeColor}
            vatSuffixBackground={productCardStyle?.vatBadgeBackground}
          />
          <FormattedPrice
            price={product.price}
            color={strikethroughPriceColor}
            fontWeight={productCardFontWeight}
            lineThrough
            productId={product.id}
            showVatSuffix={false}
          />
        </>
      );
    }

    if (discountType === 'PRICE_LIST') {
      return (
        <>
          <FormattedPrice
            price={product.price}
            color="red"
            productId={product.id}
            fontWeight={productCardFontWeight}
            showVatSuffix
            vatSuffixSize="sm"
            vatSuffixColor={productCardStyle?.vatBadgeColor}
            vatSuffixBackground={productCardStyle?.vatBadgeBackground}
          />
          <FormattedPrice
            price={product.price}
            color={strikethroughPriceColor}
            fontWeight={productCardFontWeight}
            lineThrough
            productId={product.id}
            showVatSuffix={false}
          />
        </>
      );
    }

    return (
      <>
        <FormattedPrice
          price={product.price}
          color="red"
          categoryId={product.parentCategoryId}
          productId={product.id}
          fontWeight={productCardFontWeight}
          showVatSuffix
          vatSuffixSize="sm"
          vatSuffixColor={productCardStyle?.vatBadgeColor}
          vatSuffixBackground={productCardStyle?.vatBadgeBackground}
        />
        <FormattedPrice
          price={product.price}
          color={mutedPriceColor}
          fontWeight={productCardFontWeight}
          lineThrough
          productId={product.id}
          showVatSuffix={false}
        />
      </>
    );
  };

  return {
    productCardVersion,
    productCardStyle,
    currency,
    shouldShowPrice,
    shouldAllowCart,
    shouldChangeImageOnHover,
    isDarkSurface,
    isVersion2ProductCard,
    mainImageSrc,
    hoverImageSrc,
    renderPriceContent,
  };
};

const useMobileViewport = () => {
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobileViewport(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  return isMobileViewport;
};

const useHoverImage = (shouldChangeImageOnHover: boolean, hoverImageSrc?: string) => {
  const [isHovering, setIsHovering] = useState(false);
  const hoverDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverDelay = () => {
    if (hoverDelayRef.current != null) {
      clearTimeout(hoverDelayRef.current);
      hoverDelayRef.current = null;
    }
  };

  useEffect(
    () => () => {
      if (hoverDelayRef.current != null) {
        clearTimeout(hoverDelayRef.current);
        hoverDelayRef.current = null;
      }
    },
    [],
  );

  const pointerHandlers = {
    onPointerEnter: (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      if (!shouldChangeImageOnHover || !hoverImageSrc) return;
      clearHoverDelay();
      hoverDelayRef.current = setTimeout(() => setIsHovering(true), HOVER_IMAGE_DELAY_MS);
    },
    onPointerLeave: (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      clearHoverDelay();
      if (shouldChangeImageOnHover) {
        setIsHovering(false);
      }
    },
  };

  return { isHovering, pointerHandlers };
};

const ProductCardListing: FC<ProductCardListingProps> = ({
  product,
  noMaxWidth,
  interactive = true,
  showAddToCart = false,
  showFavorites = false,
  contentVariant = 'light',
  addToCartButtonType = MyCartActionButtonsEnum.PRODUCT_CARD,
  priority = false,
}) => {
  const isInteractiveReady = useDeferUntilIdle({ timeout: 2500 });
  const isMobileViewport = useMobileViewport();
  const {
    productCardVersion,
    productCardStyle,
    currency,
    shouldShowPrice,
    shouldAllowCart,
    shouldChangeImageOnHover,
    isDarkSurface,
    isVersion2ProductCard,
    mainImageSrc,
    hoverImageSrc,
    renderPriceContent,
  } = useProductCardListingPresentation(product, contentVariant);
  const { isHovering, pointerHandlers } = useHoverImage(shouldChangeImageOnHover, hoverImageSrc);
  const showCartButton = (interactive || showAddToCart) && (shouldAllowCart || showAddToCart);

  return (
    <ProductCardNavigationWrapper
      href={`/${product.locale}/product/${product.meta.url}`}
      interactive={interactive}
    >
      <Card
        className={cn(
          'relative h-full min-h-[340px] overflow-hidden rounded-2xl bg-transparent p-0 shadow-none @lg:min-h-0 @lg:p-2',
          interactive && 'cursor-pointer',
          {
            'bg-default-50': productCardVersion === ProductCardVersions.VERSION_1,
            'max-w-[258px]': !noMaxWidth,
          },
        )}
        data-testid="product-card__card"
      >
        <CardBody className="overflow-visible p-0">
          <div
            className="relative aspect-square w-full overflow-hidden rounded-2xl"
            data-testid="product-card__image-container"
            {...pointerHandlers}
          >
            <div
              className={cn('h-full w-full transition-opacity duration-300', {
                'opacity-0': isHovering && hoverImageSrc,
              })}
            >
              <ListingProductImage
                src={mainImageSrc}
                alt={product.name}
                priority={priority}
                data-testid="product-card__image"
              />
            </div>
            {hoverImageSrc && (
              <div
                className={cn(
                  'absolute inset-0 h-full w-full transition-opacity duration-300',
                  isHovering ? 'opacity-100' : 'opacity-0',
                )}
              >
                <ListingProductImage src={hoverImageSrc} alt={product.name} />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1 px-2 py-3" data-testid="product-card__details">
            <p
              className={cn(
                'line-clamp-2 text-sm font-medium',
                !productCardStyle?.titleColor && (isDarkSurface ? 'text-white' : 'text-black'),
                {
                  'font-bold': isVersion2ProductCard,
                },
              )}
              style={
                productCardStyle?.titleColor ? { color: productCardStyle.titleColor } : undefined
              }
              title={product.name}
              data-testid="product-card__title"
            >
              {product.name}
            </p>

            <div
              className={cn('flex items-center gap-2', { hidden: !shouldShowPrice })}
              data-testid="product-card__price-and-button"
            >
              {renderPriceContent()}
            </div>
          </div>
        </CardBody>
        {showCartButton && (
          <CardFooter className="px-2 pt-0 pb-3">
            {isInteractiveReady ? (
              <ProductCardAddToCartButton
                product={product}
                showAddToCart={showAddToCart}
                shouldAllowCart={Boolean(shouldAllowCart)}
                isMobileViewport={isMobileViewport}
                currency={currency}
                addToCartButtonType={addToCartButtonType}
              />
            ) : (
              <Button
                color="primary"
                className="pointer-events-none invisible w-full min-w-0"
                size={isMobileViewport ? 'sm' : 'md'}
                tabIndex={-1}
                aria-hidden="true"
                isDisabled
              >
                {'\u00A0'}
              </Button>
            )}
          </CardFooter>
        )}
        {isInteractiveReady && (
          <ProductCardInteractiveActions
            product={product}
            interactive={interactive}
            showAddToCart={showAddToCart}
            showFavorites={showFavorites}
            showCartButton={showCartButton}
            shouldAllowCart={Boolean(shouldAllowCart)}
            isMobileViewport={isMobileViewport}
            currency={currency}
            addToCartButtonType={addToCartButtonType}
            includeCartFooter={false}
          />
        )}
      </Card>
    </ProductCardNavigationWrapper>
  );
};

export default ProductCardListing;
